import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Mail, Phone, MapPin } from "lucide-react";

const STATUS_COLOR = {
  Pending: "bg-amber-100 text-amber-800",
  Approved: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-700",
};

export default function IBOApplications() {
  const [rows, setRows] = useState([]);
  const [uplines, setUplines] = useState([]);
  const [status, setStatus] = useState("Pending");
  const [openDlg, setOpenDlg] = useState(false);
  const [target, setTarget] = useState(null);
  const [action, setAction] = useState("approve"); // "approve" | "reject"
  const [note, setNote] = useState("");
  const [uplineId, setUplineId] = useState("");

  const load = useCallback(() => {
    api.get(`/ibo/applications?status=${status}`)
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [status]);
  const loadUplines = useCallback(() => {
    api.get(`/ibo/uplines-eligible`)
      .then(({ data }) => setUplines(data))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); loadUplines(); }, [load, loadUplines]);

  const openAction = (row, act) => {
    setTarget(row);
    setAction(act);
    setNote("");
    setUplineId("");
    setOpenDlg(true);
  };

  const submit = async () => {
    if (!target) return;
    try {
      const fd = new FormData();
      fd.append("note", note);
      if (action === "approve" && uplineId) fd.append("upline_ibo_id", uplineId);
      await api.post(`/ibo/applications/${target.id}/${action}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Application ${action}d`);
      setOpenDlg(false);
      setTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">IBO Applications</h1>
          <p className="text-sm text-muted-foreground">
            Review new IBO signups from the public registration page and approve or reject each.
          </p>
        </div>
        <div className="w-48">
          <Label className="text-xs">Filter</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="ibo-app-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="Rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-40">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-10" data-testid="ibo-apps-empty">
                    No {status.toLowerCase()} applications.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`ibo-app-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-xs">
                    <div className="inline-flex items-center gap-1"><Mail className="w-3 h-3 text-muted-foreground" />{r.email}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="inline-flex items-center gap-1"><Phone className="w-3 h-3 text-muted-foreground" />{r.phone}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="inline-flex items-start gap-1">
                      <MapPin className="w-3 h-3 text-muted-foreground mt-0.5" />
                      <span>{[r.city, r.district, r.state].filter(Boolean).join(", ")}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-block px-2 py-0.5 text-[10px] rounded ${STATUS_COLOR[r.status] || "bg-gray-100 text-gray-700"}`}>
                      {r.status}
                    </span>
                    {r.review_note && (
                      <div className="text-[10px] text-muted-foreground mt-1 max-w-[200px]">
                        “{r.review_note}”
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.status === "Pending" ? (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openAction(r, "approve")} data-testid={`ibo-approve-${r.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-green-600" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openAction(r, "reject")} data-testid={`ibo-reject-${r.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1 text-red-600" /> Reject
                        </Button>
                      </div>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">
                        by {r.reviewed_by_name || "admin"}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={openDlg} onOpenChange={setOpenDlg}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{action === "approve" ? "Approve IBO Application" : "Reject IBO Application"}</DialogTitle>
          </DialogHeader>
          {target && (
            <div className="text-sm space-y-1">
              <div><b>{target.name}</b> · <span className="font-mono text-xs">{target.email}</span></div>
              <div className="text-xs text-muted-foreground">{[target.city, target.district, target.state].filter(Boolean).join(", ")}</div>
            </div>
          )}
          <div className="space-y-1">
            <Label>{action === "approve" ? "Welcome note (optional)" : "Reason (optional)"}</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="ibo-app-note" />
          </div>
          {action === "approve" && (
            <div className="space-y-1">
              <Label>Upline (optional)</Label>
              <Select value={uplineId || "__none__"} onValueChange={(v) => setUplineId(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="ibo-app-upline"><SelectValue placeholder="No upline (top-level)" /></SelectTrigger>
                <SelectContent className="max-h-[60vh]">
                  <SelectItem value="__none__">— No upline (root IBO)</SelectItem>
                  {uplines.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.role}) — {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Pick who this IBO reports to in the pyramid. Leave blank if they're a top-level IBO.
                Admins can also be uplines.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDlg(false)}>Cancel</Button>
            <Button
              onClick={submit}
              data-testid="ibo-app-confirm"
              className={action === "reject" ? "bg-red-600 hover:bg-red-700" : ""}
            >
              {action === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
